import { useAlgoliaClient } from "@hooks/useAlgoliaClient";
import { AND_SYMBOL } from "@lib/constants/filtersConstants";
import type { FC, ReactNode } from "react";
import { Configure, InstantSearch } from "react-instantsearch";
import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";
import singletonRouter from "next/router";
import { getAlgoliaUserToken } from "@lib/analytics/getAlgoliaUserToken";
import { useStore } from "@store/index";
import type { InstantSearchOptions } from "instantsearch.js";

interface AlgoliaProviderProps {
    children: ReactNode;
    pagePath?: string;
    locale?: string;
    mode?: "products" | "stores";
    aroundLatLng?: string;
    facetFilters?: string[];
    customRouting?: InstantSearchOptions["routing"];
    hitsPerPage?: number;
}

export const AlgoliaProvider: FC<AlgoliaProviderProps> = ({
    children,
    pagePath,
    locale,
    mode,
    facetFilters,
    customRouting,
    aroundLatLng,
    hitsPerPage,
}) => {
    const { indexName, searchClient } = useAlgoliaClient(locale, mode);
    const routing = {
        router: createInstantSearchRouterNext({
            singletonRouter,
            serverUrl: `https://${process.env.NEXT_PUBLIC_HOSTNAME}/${locale}${pagePath}`,
            routerOptions: {
                cleanUrlOnDispose: true,
            },
        }),
        stateMapping: {
            stateToRoute(uiState) {
                const indexUiState = uiState[indexName] || {};
                const route = {
                    query: indexUiState.query,
                    sortBy: indexUiState.sortBy && indexUiState.sortBy.split(`${indexName}_`)[1],
                };

                const keys = Object.keys(indexUiState?.refinementList ?? {});

                keys?.forEach((facetName) => {
                    route[facetName] =
                        indexUiState?.refinementList &&
                        indexUiState?.refinementList[facetName] &&
                        indexUiState?.refinementList[facetName].join(AND_SYMBOL);
                });

                return route;
            },

            routeToState(routeState) {
                const refinementList = {};
                const facetNames = Object.keys(routeState).filter(
                    (it) => it !== "sortBy" && it !== "page" && it !== "query"
                );

                facetNames?.forEach((facetName) => {
                    const item = routeState[facetName];
                    if (!item) return;
                    if (Array.isArray(item)) {
                        item.forEach((element) => {
                            if (element?.includes(AND_SYMBOL)) {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], ...element.split(AND_SYMBOL)]
                                    : element.split(AND_SYMBOL);
                            } else {
                                refinementList[facetName] = refinementList[facetName]
                                    ? [...refinementList[facetName], element]
                                    : [element];
                            }
                        });
                    } else {
                        refinementList[facetName] = item.split(AND_SYMBOL);
                    }
                });

                return {
                    [indexName]: {
                        sortBy: routeState.sortBy && `${indexName}_${routeState.sortBy}`,
                        page: routeState.page,
                        query: routeState.query,
                        refinementList,
                    },
                };
            },
        },
    };

    const me = useStore((state) => state.data.me);

    const algoliaUserToken = getAlgoliaUserToken(me);

    return (
        <InstantSearch
            searchClient={searchClient}
            indexName={indexName}
            routing={customRouting ?? routing}
            future={{
                preserveSharedStateOnUnmount: true,
            }}
        >
            <Configure
                // @ts-ignore
                hitsPerPage={hitsPerPage}
                aroundLatLng={aroundLatLng}
                clickAnalytics
                facetFilters={facetFilters ?? ["hasStock:true"]}
                userToken={algoliaUserToken}
            />
            {children}
        </InstantSearch>
    );
};

export default AlgoliaProvider;
